import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';
import type {
  GetLsproComplianceSettingsQuery,
  StandardLanguage,
  UpdateLocalizedDataProtectionEntryInput,
} from 'src/generated/graphql-manager';

import {
  Heading,
  InputDescription,
  Paragraph,
  SaveButton,
  Select,
  SelectionInputLayout,
  Tab,
  Tabs,
  Toggle,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ComplianceLinkEditor } from './compliance-link-editor';
import { LanguageEditor } from './language-editor';
import {
  useGetLsproComplianceSettingsQuery,
  useUpdateLiveStreamProComplianceSettingsMutation,
} from '../../../../generated/graphql-manager';
import { getLanguageKey, slateToHtml } from '../../../../utils';
import { useCurrentUser } from '../../../hooks/current-user';

import managerStyles from '../../../manager.module.css';
import styles from '../channel-settings.module.css';

function extractLanguages(data: GetLsproComplianceSettingsQuery | undefined): StandardLanguage[] {
  return (
    data?.lsproSettings?.languages?.reduce((accumulator: StandardLanguage[], language: any) => {
      if (language.__typename === 'StandardLanguage') {
        accumulator.push(language);
      }
      return accumulator;
    }, []) || []
  );
}

export function CompliancePage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();
  const [complianceSettingsLanguages, setComplianceSettingsLanguages] = useState<StandardLanguage[]>([]);

  // Tab indices
  const [complianceTabIndex, setComplianceTabIndex] = useState(0);
  const [dataProtectionTabIndex, setDataProtectionTabIndex] = useState(0);

  // Compliance form state
  const [complianceLinksEntries, setComplianceLinksEntries] = useState<UpdateLocalizedDataProtectionEntryInput[]>([]);
  const [complianceLinksEditorRefreshKeys, setComplianceLinksEditorRefreshKeys] = useState<number[]>([]);

  // Data protection state
  const [dataProtectionEntries, setDataProtectionEntries] = useState<UpdateLocalizedDataProtectionEntryInput[]>([]);
  const [dataProtectionEditorRefreshKeys, setDataProtectionEditorRefreshKeys] = useState<number[]>([]);

  // Compliance query
  const { data, loading } = useGetLsproComplianceSettingsQuery({
    variables: { lsproId },
    onCompleted(data) {
      const languages = extractLanguages(data);
      const complianceFormEntries = data?.lsproSettings?.complianceForm?.entries || [];

      const filteredComplianceEntries = complianceFormEntries
        .filter((entry) => entry.language.__typename === 'StandardLanguage')
        .map((entry) => ({
          language: entry.language.__typename === 'StandardLanguage' ? entry.language.code : '',
          text: entry.text,
        }));

      setComplianceLinksEntries(filteredComplianceEntries);

      const dataProtectionEntries = data?.lsproSettings?.dataProtectionForm?.entries || [];
      const filteredDataProtectionEntries = dataProtectionEntries
        .filter((entry) => entry.language.__typename === 'StandardLanguage')
        .map((entry) => ({
          language: entry.language.__typename === 'StandardLanguage' ? entry.language.code : '',
          text: entry.text,
        }));

      setDataProtectionEntries(filteredDataProtectionEntries);

      const filteredLanguages = languages.filter((language) =>
        complianceFormEntries.some((entry) => {
          if (entry.language.__typename === 'StandardLanguage') {
            return entry.language.code === language.code;
          }
          return false;
        })
      );

      setComplianceSettingsLanguages(filteredLanguages);
      setComplianceLinksEditorRefreshKeys(languages.map(() => Math.random()));
      setDataProtectionEditorRefreshKeys(languages.map(() => Math.random()));
    },
  });

  const areImprintLinksEnabled = Boolean(data?.lsproSettings?.includeDefaultComplianceForm);

  const languages = useMemo(() => extractLanguages(data), [data]);

  const languagesOptions = languages.map((language) => ({
    key: language.code,
    label: t(`manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`),
  }));

  const languagesValue = languagesOptions.filter((option) =>
    complianceSettingsLanguages.some((language) => language.code === option?.key)
  );

  // Compliance & data protection mutation
  const [updateLsproComplianceSettings, { loading: updateComplianceLoading }] =
    useUpdateLiveStreamProComplianceSettingsMutation();

  const isSaving = updateComplianceLoading;

  const handleEnableImprintLinks = async (value: boolean) => {
    await updateLsproComplianceSettings({
      variables: {
        input: {
          lsproId,
          includeDefaultComplianceForm: value,
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            includeDefaultComplianceForm: () => value,
          },
        });
      },
    });
  };

  const handleSelectedLanguageChange = (options: SelectOption | SelectOption[]) => {
    const normalizedOptions = Array.isArray(options) ? options : [options];

    const selectedLanguages = languages.filter((language) =>
      normalizedOptions.some((option) => option.key === language.code)
    );

    const activeComplianceLanguage = complianceSettingsLanguages[complianceTabIndex]?.code;
    const newComplianceIndex = selectedLanguages.findIndex((l) => l.code === activeComplianceLanguage);
    setComplianceTabIndex(newComplianceIndex >= 0 ? newComplianceIndex : 0);

    const activeDataProtectionLanguage = complianceSettingsLanguages[dataProtectionTabIndex]?.code;
    const newDataProtectionIndex = selectedLanguages.findIndex((l) => l.code === activeDataProtectionLanguage);
    setDataProtectionTabIndex(newDataProtectionIndex >= 0 ? newDataProtectionIndex : 0);

    setComplianceSettingsLanguages(selectedLanguages);

    const updatedEntries = normalizedOptions.map(
      (option) =>
        dataProtectionEntries.find((entry) => entry.language === option.key) || { language: option.key, text: '' }
    );
    setDataProtectionEntries(updatedEntries);

    const updatedComplianceEntries = normalizedOptions.map(
      (option) =>
        complianceLinksEntries.find((entry) => entry.language === option.key) || { language: option.key, text: '' }
    );
    setComplianceLinksEntries(updatedComplianceEntries);
  };

  const handleOnComplianceChange = (data: string, languageCode: StandardLanguage['code']) => {
    setComplianceLinksEntries((prevEntries) => {
      const updatedEntries = [...prevEntries];
      const existingEntryIndex = prevEntries.findIndex((entry) => entry.language === languageCode);

      updatedEntries[existingEntryIndex] = {
        ...updatedEntries[existingEntryIndex],
        text: data,
      };

      return updatedEntries;
    });
  };

  const handleOnDataProtectionChange = (data: string, languageCode: StandardLanguage['code']) => {
    setDataProtectionEntries((prevEntries) => {
      const updatedEntries = [...prevEntries];
      const existingEntryIndex = prevEntries.findIndex((entry) => entry.language === languageCode);

      updatedEntries[existingEntryIndex] = {
        ...updatedEntries[existingEntryIndex],
        text: data,
      };

      return updatedEntries;
    });
  };

  const handleTabChange = useCallback((index: number) => {
    setDataProtectionTabIndex(index);
  }, []);

  const handleComplianceTabChange = useCallback((index: number) => {
    setComplianceTabIndex(index);
  }, []);

  const handleSave = async () => {
    const entries = complianceSettingsLanguages.map((language) => {
      const complianceEntry = complianceLinksEntries.find((entry) => entry.language === language.code);
      const dataProtectionEntry = dataProtectionEntries.find((entry) => entry.language === language.code);

      return {
        language: language.code,
        complianceFormText: complianceEntry?.text || '',
        dataProtectionText: dataProtectionEntry?.text || '',
      };
    });

    await updateLsproComplianceSettings({
      variables: {
        input: {
          lsproId,
          complianceSettingsForm: { entries },
        },
      },
    });
  };

  return (
    <main className={classNames(managerStyles.main, styles.wrapper)} data-testid="compliance-page">
      <Heading className={managerStyles.grayText} data-testid="compliance-heading">
        {t('manager.channelSettings.compliance.heading')}
      </Heading>

      <Paragraph data-testid="compliance-paragraph">{t('manager.channelSettings.compliance.paragraph')}</Paragraph>

      <SelectionInputLayout
        hintMessage={t('manager.channelSettings.compliance.movingimageLinksHint')}
        checked={areImprintLinksEnabled}
        variant="ghost"
        inputElement={
          <Toggle
            data-testid="enable-imprint-links-toggle"
            checked={areImprintLinksEnabled}
            onChange={() => handleEnableImprintLinks(!areImprintLinksEnabled)}
          >
            {t('manager.channelSettings.compliance.movingimageLinksInfo')}
          </Toggle>
        }
      />

      <InputDescription
        label={t('manager.channelSettings.compliance.languageLabel')}
        helpMessage={t('manager.channelSettings.compliance.languageHelpMessage')}
      >
        <Select
          data-testid="compliance-language-select"
          optionsIdProperty="key"
          value={languagesValue}
          options={languagesOptions}
          onChange={handleSelectedLanguageChange}
          placeholder={t('manager.channelSettings.compliance.languagePlaceholder')}
        />
      </InputDescription>

      {complianceSettingsLanguages && complianceSettingsLanguages.length === 1 && (
        <>
          <ComplianceLinkEditor
            refreshKey={complianceLinksEditorRefreshKeys[0]}
            language={complianceSettingsLanguages[0].code}
            content={slateToHtml(complianceLinksEntries[0]?.text)}
            disabled={loading || isSaving}
            onChange={(data) => handleOnComplianceChange(data, complianceSettingsLanguages[0].code)}
          />

          <LanguageEditor
            key={dataProtectionEditorRefreshKeys[0]}
            language={complianceSettingsLanguages[0].code}
            description={slateToHtml(dataProtectionEntries[0]?.text)}
            onDescriptionChange={(data) => handleOnDataProtectionChange(data, complianceSettingsLanguages[0].code)}
          />
        </>
      )}

      {complianceSettingsLanguages && complianceSettingsLanguages.length > 1 && (
        <>
          <InputDescription label={t('manager.channelSettings.compliance.editor.label')}>
            <Tabs
              selectedTabIndex={complianceTabIndex}
              compact={languages.length <= 4}
              onSelectTab={handleComplianceTabChange}
              data-testid="compliance-tabs"
              className={styles.tabs}
            >
              {complianceSettingsLanguages.map((language, index) => {
                const translatedLanguage = t(
                  `manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`
                );

                return (
                  <Tab label={translatedLanguage} key={language.code} data-testid={`compliance-tab-${language.code}`}>
                    <ComplianceLinkEditor
                      key={complianceLinksEditorRefreshKeys[index]}
                      language={language.code}
                      content={slateToHtml(
                        complianceLinksEntries.find((entry) => entry.language === language.code)?.text
                      )}
                      onChange={(data) => handleOnComplianceChange(data, language.code)}
                      disabled={loading || isSaving}
                    />
                  </Tab>
                );
              })}
            </Tabs>
          </InputDescription>

          <InputDescription label={t('manager.channelSettings.dataProtection.label')}>
            <Tabs
              selectedTabIndex={dataProtectionTabIndex}
              compact={languages.length <= 4}
              onSelectTab={handleTabChange}
              data-testid="data-protection-tabs"
              className={styles.tabs}
            >
              {complianceSettingsLanguages.map((language, index) => {
                const translatedLanguage = t(
                  `manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`
                );

                return (
                  <Tab
                    label={translatedLanguage}
                    key={language.code}
                    data-testid={`data-protection-tab-${language.code}`}
                  >
                    <LanguageEditor
                      key={dataProtectionEditorRefreshKeys[index]}
                      language={language.code}
                      description={slateToHtml(
                        dataProtectionEntries.find((entry) => entry.language === language.code)?.text
                      )}
                      onDescriptionChange={(data) => handleOnDataProtectionChange(data, language.code)}
                    />
                  </Tab>
                );
              })}
            </Tabs>
          </InputDescription>
        </>
      )}

      <div className={managerStyles.buttons}>
        <SaveButton
          data-testid="compliance-submit-button"
          onClick={handleSave}
          saving={isSaving}
          disabled={loading || isSaving}
        />
      </div>
    </main>
  );
}
